import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginError, reset, resetError } from 'actions/user';
import { SaveButton, Loading } from '@salesperformanceint/e4shared';
import queryString from 'query-string';
import { apiPost } from 'services';

export const PasswordReset = ({ location, history }) => {
    const dispatch = useDispatch();
    const [password, setPassword] = React.useState('');
    const [valid, setValid] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [hasExpired, setHasExpired] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    const passwordField = React.useRef(null);

    const errorMessage = useSelector(state => state.user.errorMessage);

    React.useEffect(() => {
        passwordField?.current && passwordField?.current.focus();
    }, [passwordField?.current]);

    const init = () => {
        const values = queryString.parse(location.search);
        dispatch(loginError(''));
        apiPost(
            'verify-password-reset',
            { token: values.token, guid: values.guid },
            null,
            resp => {
                setHasExpired(false);
                setIsLoading(false);
            },
            err => {
                dispatch(resetError('This password reset link has expired'));
                setHasExpired(true);
                setIsLoading(false);
            }
        );
    };

    React.useMemo(() => {
        init();
    }, []);

    const checkSubmit = (e, callback) => {
        if (e.key === 'Enter') {
            callback();
        }
    };

    React.useEffect(() => {
        const exp = '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(.*){8,}$';
        const regEx = new RegExp(exp);
        setValid(regEx.test(password));
    }, [password]);

    const resetPassword = () => {
        const values = queryString.parse(location.search);
        const entity = {
            password: password,
            email: values.email,
            token: values.token,
            guid: values.guid,
        };

        dispatch(
            reset(entity, isSuccess => {
                if (isSuccess) {
                    setSuccess(true);
                }

                setSaving(false);
            })
        );
    };

    if (isLoading) {
        return <Loading />;
    }

    const login = () => {
        history.push('/develop/performance-dashboard');
    };

    return (
        <div className="login-box">
            <div className="login-logo">
                <img alt="Richardson Sales Performance" src="/dist/img/richardson_sp.png" loading="lazy" />
            </div>
            <div className="login-body">
                {success ? (
                    <>
                        <p>Great news! Your password has been successfully updated.</p>
                        <p>Please login now to start using the site.</p>
                        <p>
                            <button onClick={login} className="btn btn-success">
                                Login
                            </button>
                        </p>
                    </>
                ) : (
                    <>
                        <p>Please enter your new password</p>
                        <p className="text">
                            It must be at least 8 characters long and contain at least one uppercase letter, one lowercase
                            letter and one number.
                        </p>
                        {errorMessage && (
                            <p className="error">
                                <i className="fa fa-solid fa-exclamation-circle text-danger mr5" />
                                {errorMessage}
                            </p>
                        )}

                        {!hasExpired && (
                            <>
                                <div className="has-feedback mb20">
                                    <input
                                        ref={passwordField}
                                        data-testid="email-field"
                                        onChange={e => setPassword(e.target.value)}
                                        onKeyUp={e => checkSubmit(e, resetPassword)}
                                        type="password"
                                        className="form-control"
                                    />
                                    <span className="glyphicon glyphicon-lock form-control-feedback" />
                                </div>
                                <div>
                                    <SaveButton
                                        saving={saving}
                                        setSaving={setSaving}
                                        disabled={!valid}
                                        onClick={resetPassword}
                                        onKeyUp={e => checkSubmit(e, resetPassword)}
                                        className="btn btn-primary"
                                    >
                                        Save new password
                                    </SaveButton>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PasswordReset;
